import { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";
import { FormikHelpers, useFormik } from "formik";
import { DateTime } from "luxon";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { transformToFormattedDate } from "~/OnboardingApp/utils";
import { requiredOver18Date } from "~/utils/validations";

type Values = {
  bornOn: string;
};

const validationSchema = Yup.object({
  bornOn: requiredOver18Date,
});

export const BirthdayPage: FC = () => {
  const { t } = useTranslation();
  const { bornOn } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();
  const initialBornOn = bornOn
    ? DateTime.fromISO(bornOn).toFormat("MM / dd / yyyy")
    : "";

  const initialValues: Values = {
    bornOn: initialBornOn || "",
  };

  const handleSubmit = (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    const { bornOn } = values;
    const date = DateTime.fromFormat(bornOn, "MM / dd / yyyy", {
      zone: "utc",
    });
    const formattedDate = (date.isValid ? date.toISO() : "") as string;

    onSubmit({ bornOn: formattedDate }, formikHelpers);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
  });

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const oldValue = formik.values.bornOn;

    const formattedDate = transformToFormattedDate({ value, oldValue });

    formik.setFieldValue("bornOn", formattedDate, true);
  };

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            description={t("onboarding.birthdayPage.description")}
            question={t("onboarding.birthdayPage.question")}
          />

          <StringInput
            id="bornOn"
            onChange={handleDateChange}
            placeholder={t("onboarding.birthdayPage.placeholder")}
          />
        </QuestionContainer>

        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
